import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getAllusers, updateAccessUser } from "../../utils/firebasefunctions";
import { actionType } from "../../context/reducer";
import { useStateValue } from "../../context/StateProvider";

const ACCESS_LIST = [
  { key: "approveDeal", label: "Approve Deal" },
  { key: "deleteDeal", label: "Delete Deal" },
  { key: "underContract", label: "Under Contract" },
  { key: "pending", label: "Pending" },
  { key: "closed", label: "Closed" },
  { key: "closedVolume", label: "Closed Volume" },
  { key: "pendingVolume", label: "Pending Volume" },
  { key: "netVolume", label: "Net Volume" },
  { key: "commissionVolume", label: "See Commissions" },
  { key: "pendingCommission", label: "Pending Commission" },
];

const AccessRole = ({ onClose, userData }) => {
  const [loading, setLoading] = useState(false);
  const [{ users }, dispatch] = useStateValue();

  const [access, setAccess] = useState(() =>
    ACCESS_LIST.reduce((acc, item) => {
      acc[item.key] = false;
      return acc;
    }, {}),
  );

  const toggleAccess = (key) => {
    setAccess((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // 🔹 Update Firebase
      await updateAccessUser(userData.id, { access: access });

      toast.success(`User Access Updated successfully!`);
      getallusers();
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to update user");
    } finally {
      setLoading(false);
    }
  };

  async function getallusers() {
    await getAllusers("users")
      .then(async (response) => {
        dispatch({
          type: actionType.STORE_ALLUSERS,
          teamleads: response.filter(function (doc) {
            return doc.role === "Admin";
          }),
          clients: response.filter(function (doc) {
            return doc.role === "Supervisor";
          }),
          employees: response,
          allusers: response,
        });
      })
      .catch((err) => {
        toast.error(String(err?.message || err));
      });
  }

  useEffect(() => {
    if (!userData?.access) return;

    setAccess((prev) => {
      const updatedAccess = { ...prev };

      ACCESS_LIST.forEach((item) => {
        if (userData.access.hasOwnProperty(item.key)) {
          updatedAccess[item.key] = Boolean(userData.access[item.key]);
        }
      });

      return updatedAccess;
    });
  }, [userData]);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="rounded-lg shadow-2xl w-[90%] lg:w-[30%] bg-white overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b bg-[#1E293B] text-white">
            <h2 className="text-xl font-semibold">Client Care Privileges</h2>
            <button
              onClick={onClose}
              className="w-9 h-9 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition hover:rotate-90"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Body */}
          <div className="my-6 mx-5 space-y-4">
            {ACCESS_LIST.map((item) => {
              const isActive = access[item.key];

              return (
                <div
                  key={item.key}
                  className="flex items-center justify-between"
                >
                  <p className="text-base">{item.label}</p>

                  <button
                    onClick={() => toggleAccess(item.key)} // ✅ important
                    className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors duration-300 ${
                      isActive ? "bg-themeColor" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-300 ${
                        isActive ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              );
            })}
          </div>
          <div className="flex justify-center my-5">
            <button
              class="relative inline-flex items-center gap-2 px-10 py-3 font-semibold text-white text-sm rounded-lg bg-gradient-to-b from-[#0f172a] to-[#1E293B] shadow-md hover:-translate-y-1 hover:shadow-lg transition duration-300 overflow-hidden group"
              onClick={handleSubmit}
            >
              <span class="absolute inset-0 bg-white/20 rounded-full scale-0 group-hover:scale-150 transition-transform duration-700 ease-out"></span>

              {loading ? (
                <svg
                  className="w-5 h-5 text-white animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
              ) : (
                <span className="relative">Submit</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AccessRole;
